import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic2, Loader2, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["Rapper / MC", "Producer", "Vocalist", "Engineer", "Instrumentalist", "Other"];
const BUDGETS = ["Love offering", "Under $500", "$500 – $1,500", "$1,500 – $5,000", "$5,000+", "Let's discuss"];

export const CollabForm = () => {
  const [mode, setMode] = useState("collab"); // 'collab' or 'booking'

  const [collab, setCollab] = useState({
    name: "",
    email: "",
    role: "",
    social_link: "",
    message: "",
  });
  const [booking, setBooking] = useState({
    name: "",
    email: "",
    event_date: "",
    venue: "",
    location: "",
    budget_range: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (which) => (k) => (e) => {
    if (which === "collab") setCollab({ ...collab, [k]: e.target.value });
    else setBooking({ ...booking, [k]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "collab") {
      if (!collab.name || !collab.email || !collab.message) {
        toast.error("Name, email, and message are required.");
        return;
      }
      setLoading(true);
      try {
        await axios.post(`${API}/collab`, collab);
        toast.success("Collab request sent. Iron sharpens iron — talk soon.");
        setCollab({ name: "", email: "", role: "", social_link: "", message: "" });
      } catch {
        toast.error("Couldn't send that. Try again in a minute.");
      } finally {
        setLoading(false);
      }
    } else {
      if (!booking.name || !booking.email || !booking.message) {
        toast.error("Name, email, and details are required.");
        return;
      }
      setLoading(true);
      try {
        await axios.post(`${API}/booking`, booking);
        toast.success("Booking inquiry received. We'll be in touch.");
        setBooking({ name: "", email: "", event_date: "", venue: "", location: "", budget_range: "", message: "" });
      } catch {
        toast.error("Couldn't send that. Try again in a minute.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <section
      id="collab"
      data-testid="collab-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
              {mode === "collab" ? "Collab" : "Book Tekreck"}
            </span>
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            {mode === "collab" ? (
              <>Make music <br /> together.</>
            ) : (
              <>Book a <br /> performance.</>
            )}
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            {mode === "collab"
              ? "Artists, musicians, producers, engineers, vocalists — if you feel pulled to collaborate, send a message. Open to features, co-production, session work, and Gospel projects especially."
              : "Churches, events, youth nights, festivals, conferences — reach out with the details and we'll see what God wants to do. Love offerings welcome."}
          </p>
          <div className="mt-10 flex items-center gap-3 text-white/70">
            <Mic2 size={18} className="text-[#D4AF37]" />
            <span className="text-sm">Based in Big Spring, TX · worldwide remote &amp; travel</span>
          </div>

          <div className="relative mt-16 hidden lg:block">
            <img
              src="https://images.pexels.com/photos/5749193/pexels-photo-5749193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Studio"
              className="w-full aspect-[4/3] object-cover border border-white/10"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          </div>
        </div>

        <div className="lg:col-span-7">
          {/* Mode toggle */}
          <div
            data-testid="collab-mode-toggle"
            className="inline-flex mb-6 border border-white/15 p-1"
          >
            <button
              data-testid="mode-collab"
              type="button"
              onClick={() => setMode("collab")}
              className={`px-5 h-10 text-xs uppercase tracking-[0.2em] transition-colors ${
                mode === "collab"
                  ? "bg-[#D4AF37] text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              Collab
            </button>
            <button
              data-testid="mode-booking"
              type="button"
              onClick={() => setMode("booking")}
              className={`px-5 h-10 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2 ${
                mode === "booking"
                  ? "bg-[#D4AF37] text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              <Calendar size={12} /> Book Tekreck
            </button>
          </div>

          <form
            data-testid={mode === "collab" ? "collab-form" : "booking-form"}
            onSubmit={onSubmit}
            className="bg-[#0e0e0e] border border-white/10 p-8 md:p-10 space-y-6"
          >
            {mode === "collab" ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" required>
                    <input data-testid="collab-name-input" value={collab.name} onChange={update("collab")("name")} placeholder="Ex. Jordan" className="input-base" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="collab-email-input" type="email" value={collab.email} onChange={update("collab")("email")} placeholder="you@studio.com" className="input-base" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="What do you do?">
                    <select data-testid="collab-role-select" value={collab.role} onChange={update("collab")("role")} className="input-base">
                      <option value="">Select a role</option>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Social or portfolio link">
                    <input data-testid="collab-social-input" value={collab.social_link} onChange={update("collab")("social_link")} placeholder="instagram.com/yourhandle" className="input-base" />
                  </Field>
                </div>
                <Field label="Message" required>
                  <textarea data-testid="collab-message-input" value={collab.message} onChange={update("collab")("message")} rows={6} placeholder="Tell me about the project, the vibe, the timeline…" className="input-base resize-none" />
                </Field>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" required>
                    <input data-testid="booking-name-input" value={booking.name} onChange={update("booking")("name")} className="input-base" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="booking-email-input" type="email" value={booking.email} onChange={update("booking")("email")} className="input-base" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Event date">
                    <input data-testid="booking-date-input" type="date" value={booking.event_date} onChange={update("booking")("event_date")} className="input-base" />
                  </Field>
                  <Field label="Budget range">
                    <select data-testid="booking-budget-select" value={booking.budget_range} onChange={update("booking")("budget_range")} className="input-base">
                      <option value="">Select a range</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Venue / organization">
                    <input data-testid="booking-venue-input" value={booking.venue} onChange={update("booking")("venue")} placeholder="Ex. Faith Chapel" className="input-base" />
                  </Field>
                  <Field label="Location (city, state)">
                    <input data-testid="booking-location-input" value={booking.location} onChange={update("booking")("location")} placeholder="Big Spring, TX" className="input-base" />
                  </Field>
                </div>
                <Field label="Event details" required>
                  <textarea data-testid="booking-message-input" value={booking.message} onChange={update("booking")("message")} rows={6} placeholder="Event type, expected audience, setlist vibe, sound provided, any special requests…" className="input-base resize-none" />
                </Field>
              </>
            )}

            <button
              data-testid={mode === "collab" ? "collab-submit-button" : "booking-submit-button"}
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-3 h-12 px-8 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#F1C40F] disabled:opacity-60 transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Sending…" : mode === "collab" ? "Send Collab Request" : "Send Booking Inquiry"}
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .input-base {
          width: 100%;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          padding: 14px 16px;
          font-size: 14px;
          font-family: inherit;
          outline: none;
          transition: border-color 0.2s;
        }
        .input-base:focus { border-color: #D4AF37; }
        .input-base::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </section>
  );
};

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </span>
    {children}
  </label>
);
